/**
 * screens/RouterScreen.tsx
 * Setup · Router Prompt (softknack) — one global versioned orchestration prompt.
 * Recreated verbatim from the design HTML (~441-480), max-width 1080.
 *
 * NOTE (2026-07-23) — INTENTIONAL STUB. What you author/save here is stored &
 * versioned, but sk-voice does NOT consume it at call time. Global behaviour/tone
 * lives in hardcoded base rules + guardrails (sk-voice/promptAssembler.ts) — same
 * for every org, and safety must not be org-editable. A separate editable global
 * prompt was judged redundant + risky (one bad edit → every call). Screen kept so
 * the surface/history exists; treat as inert until deliberately wired as the
 * [ROUTER PROMPT] layer in promptAssembler. See voiceSetupRoutes.ts ROUTER_SCOPE.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, Pressable, TextInput } from 'react-native';
import { useTheme } from '../context/themeContext';
import Icon from '../components/icon';
import { Chip, Button, SectionLabel, ScreenBody } from '../components/voiceUi';
import { listRouterVersions, createRouterVersion, setRouterCurrent, RouterVersion } from '../services/voice/voiceService';

type RVersion = RouterVersion;

export default function RouterScreen() {
  const { theme } = useTheme();
  const c = theme.colors;

  const [versions, setVersions] = useState<RVersion[]>([]);
  // null until first load resolves; then defaults to the newest version.
  const [selectedVer, setSelectedVer] = useState<number | null>(null);
  const [draft, setDraft] = useState<string>('');

  const load = useCallback(async (pickVer?: number) => {
    const { data } = await listRouterVersions();
    setVersions(data);
    // Re-select the requested version (after save/set-current), else keep the
    // current selection, else fall back to the newest version.
    setSelectedVer(prev => {
      const target = pickVer ?? prev;
      const found = data.find(v => v.v === target) ?? data[0];
      if (found) setDraft(found.body);
      return found ? found.v : null;
    });
  }, []);
  // Plain effect — the console shell has no React Navigation (no useFocusEffect).
  useEffect(() => { void load(); }, [load]);

  const sel = versions.find(v => v.v === selectedVer) || versions[0];
  const selIsCurrent = !!sel?.isCurrent;
  const dirty = !!sel && draft !== sel.body;

  const pick = (v: RVersion) => { setSelectedVer(v.v); setDraft(v.body); };
  const setCurrent = async (n: number) => { await setRouterCurrent(n); await load(n); };
  const saveNew = async () => {
    const created = await createRouterVersion(draft, 'Saved from console');
    // Backend cuts the new version and sets it current; reselect it if we can.
    await load(created?.versionNo);
  };

  const sorted = versions.slice().sort((a, b) => b.v - a.v);

  return (
    <ScreenBody maxWidth={1080}>
      {/* Info banner */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 13, paddingVertical: 10, borderWidth: 1, borderColor: c.line, borderRadius: 9, backgroundColor: c.paper2, marginBottom: 16 }}>
        <Icon name="info" size={15} color={c.ink3} />
        <Text style={{ fontSize: 12, color: c.ink3, fontFamily: theme.font.sans }}>One global prompt for all orgs today · per-org customization can be layered on later.</Text>
      </View>

      <View style={{ flexDirection: 'row', gap: 16, alignItems: 'flex-start' }}>
        {/* LEFT: editor */}
        <View style={{ flex: 1, backgroundColor: c.card, borderWidth: 1, borderColor: c.line, borderRadius: 10, overflow: 'hidden', ...(theme.shadows.sm as object) }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8, paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: c.line }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 9 }}>
              <Text style={{ fontWeight: '600', fontSize: 13.5, color: c.ink, fontFamily: theme.font.sans }}>Router prompt v{sel?.v}</Text>
              <Chip kind="mono">carries safety envelope</Chip>
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              {dirty && <Text style={{ fontSize: 11, color: c.ink3, fontStyle: 'italic', fontFamily: theme.font.sans }}>unsaved edits</Text>}
              {selIsCurrent && <Chip kind="solid">Current</Chip>}
            </View>
          </View>
          <TextInput
            value={draft}
            onChangeText={setDraft}
            multiline
            style={{ padding: 16, fontFamily: theme.font.mono, fontSize: 12, lineHeight: 12 * 1.6, color: c.ink, backgroundColor: c.card, minHeight: 420, textAlignVertical: 'top' } as any}
          />
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 16, paddingVertical: 12, borderTopWidth: 1, borderTopColor: c.line, backgroundColor: c.paper2 }}>
            <Text style={{ fontSize: 11.5, color: c.ink3, fontFamily: theme.font.sans }}>Saving cuts a new version and sets it current.</Text>
            <View style={{ flex: 1 }} />
            <Button kind="primary" label="Save new version" onPress={saveNew} />
          </View>
        </View>

        {/* RIGHT: version timeline */}
        <View style={{ width: 320 }}>
          <SectionLabel style={{ marginBottom: 10 }}>Version timeline · {versions.length}</SectionLabel>
          <View style={{ flexDirection: 'column', gap: 8 }}>
            {sorted.map(v => {
              const isSel = v.v === selectedVer;
              return (
                <Pressable key={v.v} onPress={() => pick(v)}
                  style={{ flexDirection: 'row', gap: 12, paddingHorizontal: 14, paddingVertical: 12, borderRadius: 9, borderWidth: 1, borderColor: isSel ? c.ink : c.line, backgroundColor: isSel ? c.paper2 : c.card }}>
                  <Text style={{ width: 32, fontFamily: theme.font.mono, fontSize: 13, fontWeight: '600', color: c.ink }}>v{v.v}</Text>
                  <View style={{ minWidth: 0, flex: 1 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7, marginBottom: 2 }}>
                      <Text style={{ fontSize: 11, color: c.ink3, fontFamily: theme.font.sans }}>{v.date}</Text>
                      {v.isCurrent && <Text style={{ fontSize: 10, fontWeight: '600', color: c.ink, fontFamily: theme.font.sans }}>● current</Text>}
                    </View>
                    <Text style={{ fontSize: 11.5, color: c.ink2, lineHeight: 11.5 * 1.4, fontFamily: theme.font.sans }}>{v.note}</Text>
                    <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 6, marginTop: 5 }}>
                      <Text style={{ fontSize: 10.5, color: c.ink4, fontFamily: theme.font.mono }}>{v.by}</Text>
                      {!v.isCurrent && (
                        <Pressable onPress={() => setCurrent(v.v)} style={{ height: 24, paddingHorizontal: 10, justifyContent: 'center', borderWidth: 1, borderColor: c.lineStrong, borderRadius: 20, backgroundColor: c.card }}>
                          <Text style={{ fontSize: 10.5, color: c.ink2, fontFamily: theme.font.sans }}>Set current</Text>
                        </Pressable>
                      )}
                    </View>
                  </View>
                </Pressable>
              );
            })}
          </View>
        </View>
      </View>
    </ScreenBody>
  );
}
